/**
 * Sarvam AI Speech-to-Text API Service
 * Handles communication with the Sarvam API for audio transcription
 * Compatible with Node.js 16.20.2
 */

const API_KEY = process.env.REACT_APP_SARVAM_API_KEY;
const API_ENDPOINT = process.env.REACT_APP_SARVAM_API_ENDPOINT;

/**
 * Transcribe audio file using Sarvam AI API
 * @param {Blob} audioBlob - Audio file as Blob object
 * @param {Object} options - Optional parameters
 * @returns {Promise<Object>} - API response with transcript
 */
export const transcribeAudio = async (audioBlob, options = {}) => {
  try {
    // Validate API Key
    if (!API_KEY) {
      throw new Error(
        'API Key not configured. Please add REACT_APP_SARVAM_API_KEY to .env file'
      );
    }

    if (!API_ENDPOINT) {
      throw new Error(
        'API Endpoint not configured. Please add REACT_APP_SARVAM_API_ENDPOINT to .env file'
      );
    }

    // Create FormData for multipart/form-data request
    const formData = new FormData();

    // Append the audio file
    // Note: The API expects the file with parameter name 'file'
    formData.append('file', audioBlob, 'audio.webm');

    // Append optional parameters if provided
    if (options.model) {
      formData.append('model', options.model);
    }
    if (options.language_code) {
      formData.append('language_code', options.language_code);
    }
    if (options.input_audio_codec) {
      formData.append('input_audio_codec', options.input_audio_codec);
    }

    console.log('Sending transcription request to:', API_ENDPOINT);
    console.log('Audio blob type:', audioBlob.type);
    console.log('Audio blob size:', audioBlob.size);
    console.log('Language code:', options.language_code);
    console.log('Model:', options.model);

    // Make the API request
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'api-subscription-key': API_KEY,
      },
      body: formData,
    });

    // Handle different response statuses
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (e) {
        // Response is not JSON
      }

      switch (response.status) {
        case 400:
          throw new Error(
            'Bad Request: Invalid audio file or parameters. ' +
              (errorData.message || '')
          );
        case 403:
          throw new Error(
            'Forbidden: Invalid API key. Please check your REACT_APP_SARVAM_API_KEY in .env file'
          );
        case 422:
          throw new Error(
            'Unprocessable Entity: Invalid audio format or encoding. ' +
              'Expected: WAV format, 16kHz sample rate. ' +
              (errorData.message || '')
          );
        case 429:
          throw new Error(
            'Quota Exceeded: Too many requests. Please try again later.'
          );
        case 500:
          throw new Error('Internal Server Error: API server error');
        case 503:
          throw new Error('Service Overloaded: API is temporarily unavailable');
        default:
          throw new Error(
            `API Error (${response.status}): ${errorData.message || 'Unknown error'}`
          );
      }
    }

    // Parse and return the response
    const data = await response.json();
    console.log('Transcription successful:', data);
    return data;
  } catch (error) {
    console.error('Transcription error:', error);
    throw error;
  }
};